import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Mail, Settings } from "lucide-react";
import { getMaintenance, meApi, getToken } from "@/lib/api";
import { resolveUrl } from "@/lib/media";

const SOCIAL_LABELS = { instagram: "Instagram", facebook: "Facebook", youtube: "YouTube", tiktok: "TikTok", linkedin: "LinkedIn", twitter: "X", x: "X" };

const MaintenancePage = ({ cfg }) => {
  const bg = cfg.bg || "#050505";
  const text = cfg.textColor || "#ffffff";
  const accent = cfg.accentColor || "#3E9BFF";
  return (
    <div data-testid="maintenance-page" className="min-h-[100dvh] flex flex-col items-center justify-center px-6 text-center relative overflow-hidden"
      style={{ backgroundColor: bg, color: text }}>
      <div className="absolute inset-0 opacity-[0.15] pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 30%, ${accent}55, transparent 60%)` }} />
      <div className="relative z-10 max-w-xl w-full">
        {cfg.logo
          ? <img src={resolveUrl(cfg.logo)} alt="Karams Studio" className="mx-auto mb-10 max-h-24 w-auto" data-testid="maintenance-logo" />
          : <div className="mb-10 text-sm tracking-[0.4em] uppercase" style={{ color: accent }}>KARAMS STUDIO</div>}
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6" data-testid="maintenance-title">{cfg.title || "Site en cours de mise à jour"}</h1>
        {(cfg.message || "").split("\n").filter(Boolean).map((p, i) => (
          <p key={i} className="text-base sm:text-lg font-light opacity-80 leading-relaxed mb-2">{p}</p>
        ))}

        {cfg.showContact && cfg.contactEmail && (
          <a href={`mailto:${cfg.contactEmail}`} data-testid="maintenance-contact"
            className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-full text-[12px] tracking-[0.12em] uppercase font-semibold transition-transform hover:scale-105"
            style={{ backgroundColor: accent, color: "#000" }}>
            <Mail size={15} /> Nous contacter
          </a>
        )}

        {(cfg.socials || []).length > 0 && (
          <div className="flex items-center justify-center gap-5 mt-10" data-testid="maintenance-socials">
            {cfg.socials.map((s, i) => (
              <a key={i} href={s.url} target="_blank" rel="noreferrer" className="text-sm opacity-70 hover:opacity-100 transition-opacity"
                style={{ color: accent }}>{SOCIAL_LABELS[(s.platform || "").toLowerCase()] || s.platform}</a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const AdminBanner = () => (
  <div data-testid="maintenance-admin-banner"
    className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[10000] flex items-center gap-3 rounded-full border border-amber-400/50 bg-black/85 backdrop-blur px-5 py-2.5 shadow-xl">
    <span className="h-2.5 w-2.5 rounded-full bg-amber-400 animate-pulse" />
    <span className="text-[11px] tracking-[0.15em] uppercase text-amber-300">Mode maintenance actif — visible par vous seul</span>
    <Link to="/admin" className="inline-flex items-center gap-1.5 text-[11px] text-white/70 hover:text-electric"><Settings size={13} /> Gérer</Link>
  </div>
);

// Gate wrapping public routes. Admin (valid token) keeps full access + preview during maintenance.
// Fails OPEN (shows the site) if the maintenance API is unreachable, to avoid accidental blackout.
// Search-engine crawlers ALWAYS see the real site (never the thin maintenance page) to avoid Soft 404 / deindexing.
const isCrawler = () => {
  if (typeof navigator === "undefined") return false;
  return /bot|crawl|spider|slurp|googlebot|google-inspectiontool|bingpreview|duckduckbot|baiduspider|yandex|facebookexternalhit|lighthouse|headless|prerender|rendertron/i.test(navigator.userAgent || "");
};

export const MaintenanceGate = ({ children }) => {
  const [state, setState] = useState({ loading: true, cfg: null, isAdmin: false });

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (isCrawler()) { if (mounted) setState({ loading: false, cfg: { enabled: false }, isAdmin: false }); return; }
      try {
        const cfg = await getMaintenance();
        if (!cfg.enabled) { if (mounted) setState({ loading: false, cfg, isAdmin: false }); return; }
        let isAdmin = false;
        if (getToken()) { try { const me = await meApi(); isAdmin = !!(me && (me.id || me.email)); } catch { isAdmin = false; } }
        if (mounted) setState({ loading: false, cfg, isAdmin });
      } catch {
        if (mounted) setState({ loading: false, cfg: { enabled: false }, isAdmin: false });
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (state.loading) return <div className="min-h-[100dvh] bg-[#050505]" />;
  if (state.cfg?.enabled && !state.isAdmin) return <MaintenancePage cfg={state.cfg} />;
  return (
    <>
      {state.cfg?.enabled && state.isAdmin && <AdminBanner />}
      {children}
    </>
  );
};
